import React, { useState } from "react";
import { NameOptions, NameResult } from "../../types";
import { API_DESIGN_NAME } from "../../constants/api";
import { useFetch } from "../../hooks/useFetch";
import { useAtom, useAtomValue } from "jotai";
import {
  nameResultsAtom,
  hasDuplicateNameAtom,
  formStateAtom,
  triesLeftAtom,
} from "../../store/store";
import { useSaveName } from "../../hooks/useSaveName";
import { useGetUnlockedNames } from "../../hooks/useGetUnlockedNames";
import { GenderList, GenderMap } from "../../constants/common";
import { useUser } from "../../hooks/useUser";
import Header from "../../components/Header/Header";
import NameGenerator from "../../components/NameGenerator/NameGenerator";
import ResultDisplay from "../../components/ResultDisplay/ResultDisplay";
import ToastService from "../../components/common/Toast/ToastService";
import "./Home.css";

const Home: React.FC = () => {
  const [results, setResults] = useAtom(nameResultsAtom);
  const hasDuplicateName = useAtomValue(hasDuplicateNameAtom);
  const [formState, setFormState] = useAtom(formStateAtom);
  const triesLeft = useAtomValue(triesLeftAtom);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState(0);
  const { request } = useFetch();
  const { handleSaveName } = useSaveName();
  const { getUnlockedNames } = useGetUnlockedNames();
  const { openLoginModal } = useUser();

  const handleGenerate = async () => {
    if (triesLeft <= 0) return;

    setGeneratingProgress(0);
    const totalSteps = 6;
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep += 1;
      setGeneratingProgress(Math.max(currentStep, totalSteps - 1));
    }, 500);

    try {
      setIsGenerating(true);
      const { data } = await request<NameResult>(API_DESIGN_NAME, {
        method: "POST",
        body: {
          firstName: formState.firstName,
          lastName: formState.lastName,
          gender: GenderMap[formState.gender],
          length: formState.length,
          ...formState.options,
        },
      });
      setResults((prev) => [data, ...prev]);
      ToastService.success("Design name successfully");
    } catch (error) {
      ToastService.error("Design name failed");
    } finally {
      setIsGenerating(false);
      clearInterval(interval);
    }
  };

  const handleTryAgain = (result: NameResult) => {
    if (result.firstName) {
      // 提取选项信息
      const retryOptions: NameOptions = {
        styles: result.styles,
        themes: result.themes,
        meanings: result.meanings,
        phonetics: result.phonetics,
      };

      // 存储表单状态
      setFormState({
        firstName: result.firstName,
        lastName: result.lastName,
        options: retryOptions,
        gender: GenderList[result.gender],
        length: result.length,
      });
    }
  };

  const handleUnlock = () => {
    getUnlockedNames();
    ToastService.success(
      "Unlock successful! Enjoy your 99 name designs.",
      4000
    );
  };

  return (
    <div className="home-page">
      <Header />

      <main className="main-content">
        <div className="content-container">
          <div className="generator-column">
            <NameGenerator
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              triesLeft={triesLeft}
              onUnlock={handleUnlock}
            />
          </div>

          <div className="results-column">
            <ResultDisplay
              results={results}
              isGenerating={isGenerating}
              generatingProgress={generatingProgress}
              onSave={handleSaveName}
              onTryAgain={handleTryAgain}
              onLogin={openLoginModal}
              hasDuplicateName={hasDuplicateName}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Home;
